import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { fetchActivityLogs } from "./api/activity_log";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";
import { Button } from "../../components/ui/button";
import { Calendar as CalendarIcon } from "lucide-react"; 
import { useBreadcrumbs } from "../../contexts/BreadcrumbsContext";
import { Alert, AlertDescription } from "components/ui/alert";

import LogSection, { Log, getActiveFilterLabel } from "./LogSection"; 

const ActivityLogPage = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  useEffect(() => {
    setBreadcrumbs([{ label: "Performance Tracker", href: "/activity-log" }]);
  }, [setBreadcrumbs]);

  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const defaultFromDate = new Date();
  const defaultToDate = new Date();
  
  const [fromDate, setFromDate] = useState<Date | undefined>(defaultFromDate);
  const [toDate, setToDate] = useState<Date | undefined>(defaultToDate);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [tempFromDate, setTempFromDate] = useState<Date | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>("Today");

  const fetchLogsByDates = async (from?: Date, to?: Date) => {
    setLoading(true);
    setError(null);
    try {
      const fromStr = from ? dayjs(from).format("YYYY-MM-DD") : undefined;
      const toStr = to ? dayjs(to).format("YYYY-MM-DD") : undefined;
      const response = await fetchActivityLogs(fromStr, toStr);
      setLogs(response.logs || []);
    } catch (err: any) {
      console.error("Failed to fetch activity logs:", err);
      setLogs([]);
      setError(err?.response?.data?.detail || err?.message || "Failed to fetch activity logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogsByDates(fromDate, toDate);
  }, [fromDate, toDate]);

  const handleQuickFilter = (label: string, days: number) => {
    setTempFromDate(null); 
    setActiveFilter(label);
    const to = new Date();
    const from = dayjs(to).subtract(days - 1, "day").toDate(); 
    setFromDate(from);
    setToDate(to);
  };

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) return;

    if (!tempFromDate) {
      setTempFromDate(selectedDate);
      setFromDate(selectedDate);
      setToDate(undefined);
      setActiveFilter("Date Range"); // Indicate manual selection started
    } else {
      const newToDate = selectedDate >= tempFromDate ? selectedDate : tempFromDate;
      const newFromDate = selectedDate < tempFromDate ? selectedDate : tempFromDate;
      
      setFromDate(newFromDate);
      setToDate(newToDate);
      setTempFromDate(null);
      setIsDatePickerOpen(false);
      
      // Check if the manual range matches a quick filter preset
      setActiveFilter(getActiveFilterLabel(newFromDate, newToDate));
    }
  };


  return (
    <div className="p-6 space-y-8 min-h-screen bg-white">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 -mt-4">
        <div className="flex items-center gap-3">
          <span className="font-medium text-gray-600 text-sm">Filter by Date Range:</span>
          <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
            <div className="flex divide-x border border-gray-200 bg-white rounded-md overflow-hidden">
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-9 px-3 justify-start text-left text-xs font-normal hover:bg-gray-50 ${!fromDate ? "text-gray-500" : ""}`}
                  onClick={() => { setIsDatePickerOpen(true)}}
                >
                  <CalendarIcon className="mr-1 h-4 w-4 text-gray-500" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-normal text-gray-400">From</span>
                    {fromDate && <span className="text-sm font-medium truncate text-gray-800">{dayjs(fromDate).format("DD MMM YYYY")}</span>}
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-9 px-3 justify-start text-left text-xs font-normal hover:bg-gray-50 ${!toDate ? "text-gray-500" : ""}`}
                  onClick={() => { setIsDatePickerOpen(true)}}
                >
                  <CalendarIcon className="mr-1 h-4 w-4 text-gray-500" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-normal text-gray-400">To</span>
                    {toDate && <span className="text-sm font-medium truncate text-gray-800">{dayjs(toDate).format("DD MMM YYYY")}</span>}
                  </div>
                </Button>
              </PopoverTrigger>
            </div>

            <PopoverContent className="w-auto p-4 bg-white rounded-lg border-gray-200">
              <h5 className="text-base font-semibold text-gray-800 border-b pb-2 mb-3 px-2">
                {tempFromDate ? "Select End Date" : "Select Start Date"}
              </h5>
              <Calendar
                mode="single"
                selected={tempFromDate || fromDate}
                onSelect={handleDateSelect}
                numberOfMonths={2}
                disabled={(date) => tempFromDate ? date < dayjs(tempFromDate).startOf('day').toDate() : false}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex flex-wrap gap-2">
          {[
            { label: "Today", days: 1 },
            { label: "Last 7 Days", days: 7 },
            { label: "Last 30 Days", days: 30 },
            { label: "Last 90 Days", days: 90 },
          ].map((btn) => {
            const isActive = activeFilter === btn.label;
            return (
              <Button
                key={btn.label}
                onClick={() => handleQuickFilter(btn.label, btn.days)}
                size="sm"
                className={`
                  h-8 px-3 text-sm font-medium rounded-md
                  border transition duration-150
                  ${isActive
                    ? "bg-gray-800 text-white shadow-sm hover:bg-black hover:text-white"
                    : "bg-gray-100 text-black hover:bg-gray-200"}
                `}
                variant="ghost">
                {btn.label}
              </Button>
            );
          })}
        </div>
      </div>
      
      <hr className="my-6 border-gray-100"/>

      {error && (
        <div className="px-1">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      )}

      <LogSection logs={logs} loading={loading} />
    </div>
  );
};

export default ActivityLogPage;